import { formatDuration } from '@/utils/formatters';

export interface ReportTask {
  id: string;
  date: string;
  title: string;
  description: string | null | undefined;
  durationMinutes: number;
  hourlyRate: number;
  currency: string;
  totalAmount: number;
  billingStatus: string;
  project: { id: string; name: string; color?: string | null } | null | undefined;
  // 1 or 2 members — see issue #8. Aggregation does not depend on members
  // (charts/tables break down by project and date), but the type stays in
  // sync with exportToExcel so the same payload flows through unchanged.
  members: ReadonlyArray<{ id: string; name: string }>;
}

export interface DayAggregate {
  date: string;
  hours: number;
}

export interface ProjectAggregate {
  projectId: string;
  projectName: string;
  color: string | null;
  hours: number;
}

export interface StackedDayAggregate {
  date: string;
  total: number;
  [projectId: string]: number | string;
}

export function aggregateByDay(entries: readonly ReportTask[]): DayAggregate[] {
  const map = new Map<string, number>();

  for (const entry of entries) {
    map.set(entry.date, (map.get(entry.date) ?? 0) + entry.durationMinutes);
  }

  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, minutes]) => ({
      date,
      hours: Number((minutes / 60).toFixed(2)),
    }));
}

export function aggregateStackedByDay(
  entries: readonly ReportTask[],
): { data: StackedDayAggregate[]; projects: ProjectAggregate[] } {
  const projects = aggregateByProject(entries);
  const dateMap = new Map<
    string,
    { total: number; byProject: Map<string, number> }
  >();

  for (const entry of entries) {
    const existing = dateMap.get(entry.date) ?? {
      total: 0,
      byProject: new Map<string, number>(),
    };
    existing.total += entry.durationMinutes;
    // Issue #10: project-less entries bucket under a synthetic id so the
    // daily total still sums everything. The "no project" pseudo-project
    // appears in the projects list (added below) so the stacked chart has
    // a column for it.
    const projectKey = entry.project?.id ?? NO_PROJECT_KEY;
    existing.byProject.set(
      projectKey,
      (existing.byProject.get(projectKey) ?? 0) + entry.durationMinutes,
    );
    dateMap.set(entry.date, existing);
  }

  const data = Array.from(dateMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, value]) => {
      const row: StackedDayAggregate = { date, total: Number((value.total / 60).toFixed(2)) };
      for (const project of projects) {
        const minutes = value.byProject.get(project.projectId) ?? 0;
        row[project.projectId] = Number((minutes / 60).toFixed(2));
      }
      return row;
    });

  return { data, projects };
}

// Synthetic id used by aggregate* helpers to bucket entries that have no
// project (issue #10). Display name in charts is "No project".
export const NO_PROJECT_KEY = '__no_project__';
export const NO_PROJECT_NAME = 'No project';

export function aggregateByProject(entries: readonly ReportTask[]): ProjectAggregate[] {
  const map = new Map<string, ProjectAggregate>();

  for (const entry of entries) {
    const key = entry.project?.id ?? NO_PROJECT_KEY;
    const name = entry.project?.name ?? NO_PROJECT_NAME;
    const color = entry.project?.color ?? null;
    const existing = map.get(key);
    if (existing) {
      existing.hours = Number((existing.hours + entry.durationMinutes / 60).toFixed(2));
    } else {
      map.set(key, {
        projectId: key,
        projectName: name,
        color,
        hours: Number((entry.durationMinutes / 60).toFixed(2)),
      });
    }
  }

  return Array.from(map.values()).sort((a, b) => b.hours - a.hours);
}

export function totalDurationMinutes(entries: readonly ReportTask[]): number {
  return entries.reduce((sum, entry) => sum + entry.durationMinutes, 0);
}

export function formatTooltipDuration(hours: number): string {
  return formatDuration(Math.round(hours * 60));
}
